import React, { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
  Grid,
  Alert,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Card,
  CardContent,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Lock as LockIcon,
  LockReset as LockResetIcon,
  Security as SecurityIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Visibility as VisibilityIcon,
  Pending as PendingIcon,
  Block as BlockIcon,
  Assessment as AssessmentIcon,
  Event as EventIcon,
  AttachMoney as AttachMoneyIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  People as PeopleIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { usePagination, useDialog, useTableFilters } from '../../hooks'
import { StatusChip, RoleChip } from '../../components/common/Chips'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'
import { organizationApi } from '../../api/organizationApi'
import { useLoading } from '../../hooks'
import { formatDate } from '../../utils/formatters'
import { exportToCSV } from '../../utils/exportUtils'
import { Download as DownloadIcon } from '@mui/icons-material'


const UsersManagement = ({ organizationId }) => {
  const [users, setUsers] = useState([])
  const { loading, startLoading, stopLoading } = useLoading()
  const navigate = useNavigate()
  const pagination = usePagination()
  const dialog = useDialog()
  const { filters, updateFilter } = useTableFilters({
    role: 'all',
    status: 'all'
  })

  const [organizations, setOrganizations] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedUser, setSelectedUser] = useState(null)
  const [originalUserData, setOriginalUserData] = useState(null)

  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('success')

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    organization_id: '',
    role: 'org_admin',
    send_email: true,
    permissions: {
      view_employees: true,
      manage_employees: false,
      view_reports: false,
      manage_attendance: false,
      manage_leaves: false,
      view_salary_data: false,
      system_configuration: false,
      user_management: false
    }
  })

  // Fetch organizations and users
  React.useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    startLoading()
    try {
      const [orgResponse, usersResponse] = await Promise.all([
        organizationApi.listOrganizations(),
        organizationApi.getOrgUsers()
      ])
      setOrganizations(orgResponse.data || [])
      setUsers(usersResponse.data || [])
    } catch (error) {
      console.error('Failed to fetch data:', error)
      setSnackbarMessage('Failed to load data')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    } finally {
      stopLoading()
    }
  }

  const handleAddUser = () => {
    setSelectedUser(null)
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      organization_id: organizationId || '',
      role: 'org_admin',
      send_email: true,
      permissions: {
        view_employees: true,
        manage_employees: false,
        view_reports: false,
        manage_attendance: false,
        manage_leaves: false,
        view_salary_data: false,
        system_configuration: false,
        user_management: false
      }
    })
    dialog.open()
  }

  const handleEditUser = async (user) => {
    setSelectedUser(user)
    try {
      const response = await organizationApi.getUserDetails(user.id)
      const detailedUser = response.data
      setFormData({
        name: detailedUser.name,
        email: detailedUser.email,
        password: '',
        confirmPassword: '',
        organization_id: detailedUser.org_id,
        role: detailedUser.role,
        send_email: false,
        permissions: detailedUser.permissions || {
          view_employees: true,
          manage_employees: false,
          view_reports: false,
          manage_attendance: false,
          manage_leaves: false,
          view_salary_data: false,
          system_configuration: false,
          user_management: false
        }
      })
      setOriginalUserData(detailedUser)
      dialog.open()
    } catch (error) {
      console.error('Failed to fetch user details:', error)
      setSnackbarMessage('Failed to fetch user details')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    }
  }

  const handleDeleteUser = async (user) => {
    if (window.confirm(`Delete user ${user.name}?`)) {
      try {
        const response = await organizationApi.deleteUser(user.id)

        // Soft delete: update local state to inactive
        setUsers(prev => prev.map(u =>
          u.id === user.id ? { ...u, status: 'inactive' } : u
        ))

        setSnackbarMessage(response.data?.detail || `User "${user.name}" deleted successfully!`)
        setSnackbarSeverity('success')
        // fetchData() // Removed to rely on local soft update
      } catch (error) {
        console.error('Failed to delete user:', error)
        setSnackbarMessage('Failed to delete user')
        setSnackbarSeverity('error')
      }
      setSnackbarOpen(true)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handlePermissionChange = (key, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: checked
      }
    }))
  }

  const handleSaveUser = async (e) => {
    e.preventDefault()

    if (!selectedUser) {
      // Create Mode
      if (formData.password !== formData.confirmPassword) {
        setSnackbarMessage('Passwords do not match')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
        return
      }

      try {
        const payload = {
          name: formData.name,
          email: formData.email,
          org_id: formData.organization_id,
          role: formData.role,
          password: formData.password,
          send_email: formData.send_email,
          permissions: formData.permissions
        }

        await organizationApi.createOrgUser(payload)
        setSnackbarMessage('User created successfully')
        setSnackbarSeverity('success')
        dialog.close()
        fetchData() // Refresh list
      } catch (error) {
        console.error('Error creating user:', error)
        setSnackbarMessage(error.response?.data?.detail || 'Failed to create user')
        setSnackbarSeverity('error')
      }
      setSnackbarOpen(true)
    } else {
      // Edit Mode
      // Check for changes
      const hasChanges =
        formData.name !== originalUserData.name ||
        formData.email !== originalUserData.email ||
        formData.role !== originalUserData.role ||
        // Check if permissions object changed deeply
        JSON.stringify(formData.permissions) !== JSON.stringify(originalUserData.permissions)

      if (!hasChanges) {
        setSnackbarMessage('No changes made')
        setSnackbarSeverity('info')
        setSnackbarOpen(true)
        return
      }

      try {
        const payload = {
          name: formData.name,
          email: formData.email,
          org_id: formData.organization_id,
          role: formData.role,
          permissions: formData.permissions
        }

        await organizationApi.updateUser(selectedUser.id, payload)
        setSnackbarMessage('User updated successfully')
        setSnackbarSeverity('success')
        dialog.close()
        fetchData() // Refresh list
      } catch (error) {
        console.error('Error updating user:', error)
        setSnackbarMessage(error.response?.data?.detail || 'Failed to update user')
        setSnackbarSeverity('error')
      }
      setSnackbarOpen(true)
    }
  }

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.org_name && user.org_name.toLowerCase().includes(searchTerm.toLowerCase()))

    const matchesRole = filters.role === 'all' || user.role === filters.role
    const matchesStatus = filters.status === 'all' || user.status === filters.status
    const matchesOrg = !organizationId || (user.org_id === parseInt(organizationId, 10)) || (user.organization_id === parseInt(organizationId, 10))

    return matchesSearch && matchesRole && matchesStatus && matchesOrg
  })

  const paginatedUsers = filteredUsers.slice(
    pagination.page * pagination.rowsPerPage,
    pagination.page * pagination.rowsPerPage + pagination.rowsPerPage
  )

  // Stats for the page
  const stats = {
    total: users.length,
    active: users.filter(u => u.status === 'active').length,
    inactive: users.filter(u => u.status === 'inactive').length,
  }

  const handleExport = () => {
    const columns = [
      { label: 'Name', key: 'name' },
      { label: 'Email', key: 'email' },
      { label: 'Organization', key: 'org_name' },
      { label: 'Role', key: 'role' },
      { label: 'Status', key: 'status' },
      { label: 'Created At', key: (row) => formatDate(row.created_at) },
      { label: 'Last Login', key: (row) => (!row.last_login || row.last_login === 'Never') ? 'Never' : formatDate(row.last_login, 'dd/MM/yyyy p') }
    ]
    exportToCSV(filteredUsers, columns, 'users-list')
  }

  return (
    <Box sx={{ p: organizationId ? 0 : 3 }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Create/Edit User Dialog */}
      <Dialog open={dialog.isOpen} onClose={dialog.close} maxWidth="md" fullWidth disableRestoreFocus>
        <DialogTitle>
          <Box sx={{ fontWeight: 'bold', fontSize: '1.5rem', mb: 0.5 }}>
            {selectedUser ? 'Edit User' : 'Create New User'}
          </Box>
          <Typography variant="caption" color="textSecondary">
            {selectedUser ? 'Update user details and permissions' : 'Create a new user account for the system'}
          </Typography>
        </DialogTitle>

        <form onSubmit={handleSaveUser}>
          <DialogContent sx={{ p: 0 }}>
            {/* Form Content Omitted for Brevity - Remains Unchanged */}
            <Box sx={{ p: 3 }}>
              {/* User Information Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight="600">
                    User Information
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      size="small"
                      required
                      margin="dense"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      size="small"
                      required
                      margin="dense"
                    />
                  </Grid>

                  {!selectedUser && (
                    <>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Password"
                          type="password"
                          value={formData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          size="small"
                          required
                          margin="dense"
                          helperText="Minimum 8 characters with letters and numbers"
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Confirm Password"
                          type="password"
                          value={formData.confirmPassword}
                          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                          size="small"
                          required
                          margin="dense"
                        />
                      </Grid>
                    </>
                  )}

                  {selectedUser && (
                    <Grid size={12}>
                      <Alert
                        severity="info"
                        sx={{ mt: 1 }}
                        icon={<LockResetIcon fontSize="small" />}
                      >
                        <Typography variant="body2">
                          Password cannot be changed here. Use the "Reset Password" option in user actions.
                        </Typography>
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Organization & Role Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BusinessIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight="600">
                    Organization & Role
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth size="small" margin="dense" required
                      sx={{
                        minWidth: 200,
                        maxWidth: 400,
                        width: '100%',
                      }}
                    >
                      <InputLabel>Organization</InputLabel>
                      <Select
                        label="Organization"
                        value={formData.organization_id}
                        onChange={(e) => handleInputChange('organization_id', e.target.value)}
                        disabled={!!organizationId}
                      >
                        {organizations.map((org) => (
                          <MenuItem key={org.id} value={org.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <BusinessIcon sx={{ mr: 1, fontSize: 18, opacity: 0.7 }} />
                              <span style={{ marginLeft: 8 }}>{org.name}</span>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth size="small" margin="dense" required
                      sx={{
                        minWidth: 200,
                        maxWidth: 400,
                        width: '100%',
                      }}
                    >
                      <InputLabel>Role</InputLabel>
                      <Select
                        label="Role"
                        value={formData.role}
                        onChange={(e) => handleInputChange('role', e.target.value)}
                      >
                        <MenuItem value="org_admin">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AdminPanelSettingsIcon sx={{ mr: 1, fontSize: 18 }} color="primary" />
                            <Box>
                              <Typography variant="body2">Organization Admin</Typography>
                              <Typography variant="caption" color="textSecondary">Full org access</Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                        <MenuItem value="hr">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 1, fontSize: 18 }} color="info" />
                            <Box>
                              <Typography variant="body2">HR</Typography>
                              <Typography variant="caption" color="textSecondary">HR access</Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pt: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.send_email}
                            onChange={(e) => handleInputChange('send_email', e.target.checked)}
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">Send welcome email</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Login credentials will be emailed
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Access Permissions Section */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LockIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight="600">
                    Access Permissions
                  </Typography>
                </Box>

                <Paper elevation={0} sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.50' : 'action.selected', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    {[
                      { label: 'View Employees', key: 'view_employees', icon: <PeopleIcon fontSize="small" /> },
                      { label: 'Manage Employees', key: 'manage_employees', icon: <EditIcon fontSize="small" /> },
                      { label: 'View Reports', key: 'view_reports', icon: <AssessmentIcon fontSize="small" /> },
                      { label: 'Manage Attendance', key: 'manage_attendance', icon: <AccessTimeIcon fontSize="small" /> },
                      { label: 'Manage Leaves', key: 'manage_leaves', icon: <EventIcon fontSize="small" /> },
                      { label: 'View Salary Data', key: 'view_salary_data', icon: <AttachMoneyIcon fontSize="small" /> },
                      { label: 'System Configuration', key: 'system_configuration', icon: <SettingsIcon fontSize="small" /> },
                      { label: 'User Management', key: 'user_management', icon: <SupervisorAccountIcon fontSize="small" /> },
                    ].map((permission) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={permission.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.permissions[permission.key] || false}
                              onChange={(e) => handlePermissionChange(permission.key, e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {permission.icon}
                              <Typography variant="body2" sx={{ ml: 1 }}>
                                {permission.label}
                              </Typography>
                            </Box>
                          }
                          sx={{ m: 0 }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button
              onClick={dialog.close}
              variant="outlined"
              sx={{ minWidth: 100 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={selectedUser ? <EditIcon /> : <AddIcon />}
              sx={{ minWidth: 120 }}
            >
              {selectedUser ? 'Update User' : 'Create User'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Main Content Wrapper */}
      <Paper sx={{
        p: organizationId ? 0 : { xs: 1, md: 3 },
        borderRadius: organizationId ? 0 : 3,
        bgcolor: organizationId ? 'transparent' : 'background.paper',
        boxShadow: organizationId ? 'none' : 3
      }}>
        <Box sx={{ mb: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight="bold">
              {organizationId ? '' : 'Users Management'}
            </Typography>
          </Box>

          {/* Quick Stats cards */}
          {!organizationId && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                  <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                      <PeopleIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.total}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Users
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                  <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                      <CheckCircleIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.active}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Active
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                  <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
                      <CancelIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.inactive}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Inactive
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Filters and Search */}
          <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
              <TextField
                placeholder="Search users..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1, width: { xs: '100%', md: 'auto' } }}
              />

              <FormControl size="small" sx={{ minWidth: 120, width: { xs: '100%', md: 'auto' } }}>
                <InputLabel>Role</InputLabel>
                <Select
                  label="Role"
                  value={filters.role}
                  onChange={(e) => updateFilter('role', e.target.value)}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="org_admin">Org Admin</MenuItem>
                  <MenuItem value="hr_manager">HR Manager</MenuItem>
                  <MenuItem value="hr">HR Executive</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="viewer">Viewer</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120, width: { xs: '100%', md: 'auto' } }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters.status}
                  onChange={(e) => updateFilter('status', e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' } }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                  sx={{ flex: { xs: 1, md: 'none' } }}
                >
                  Export
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddUser}
                  sx={{ flex: { xs: 1, md: 'none' } }}
                >
                  Create User
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Users Table */}
        <Paper variant="outlined" sx={{ borderRadius: 2, boxShadow: organizationId ? 'none' : undefined, overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableSkeleton columns={7} rows={5} avatar />
                ) : paginatedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="textSecondary">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {user.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography fontWeight="medium">{user.name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography variant="body2">{user.org_name || 'N/A'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <RoleChip role={user.role} />
                      </TableCell>
                      <TableCell>
                        <StatusChip status={user.status} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(user.last_login)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditUser(user)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteUser(user)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={pagination.handleChangePage}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          />
        </Paper>
      </Paper>
    </Box>
  )
}

export default UsersManagement